import { FunctionComponent } from 'react';
import { isAdvisor, getUser, isAdmin, isSupport, isExecutive } from '../../../store/User';
import { Permission } from '../../../store/GeneralStore';
import { ContactUsLink } from '../ContactUs/ContactUsLink';
import { AuthenticatedUser } from '../../../types';
import { AdminPageLink } from '../AdminPageLink';
import { TeamSettingsPageLink } from '../TeamSettingsPageLink';
import { PermissibleRender } from '../PermissibleRender';
import { TextColor, TextSize, TextSpan } from '../../Atoms/Text';
import DropdownMenu from '../Menu/DropdownMenu';
import { useSession } from '../../../Api/useSession';
import { AccountDropdown } from './AccountDropdown';
import { NotificationCenter } from '../Notifications/NotificationCenter';
import { useNavigate } from 'react-router-dom';
import { IconType } from '../../Atoms/Icon';

const AccountScopeHeader = ({ user }: { user: AuthenticatedUser }) => {
  const showAccountSelect = [
    isAdvisor(user),
    isExecutive(user),
    isAdmin(user),
    isSupport(user),
    user?.accounts?.length > 1
  ].some((r) => r);

  return (
    <>
      {showAccountSelect && (
        <div
          className="flex items-center space-x-2"
          data-testid="account-select">
          <TextSpan className="text-[.90rem] text-nav-200">Account(s)</TextSpan>
          <AccountDropdown />
        </div>
      )}
      {!showAccountSelect && <span className="text-nav-200">{user.account_name}</span>}
    </>
  );
};

export const NavBarTabs: FunctionComponent = () => {
  const { signOut } = useSession();
  const user = getUser();
  const navigate = useNavigate();

  if (!user.authenticated) return <div className="grow flex items-center" />;

  const menuOptions = [];
  menuOptions.push({
    title: `${user.first_name || ''} ${user.last_name || ''}`,
    onClick: () => navigate('/user-settings'),
    fontColor: 'indigo' as TextColor,
    dataCy: 'user-settings-link'
  });
  menuOptions.push({
    title: user.email,
    onClick: () => {},
    fontColor: 'grey6' as TextColor,
    fontSize: 'xs' as TextSize
  });
  menuOptions.push({
    title: 'Settings',
    onClick: () => navigate('/user-settings'),
    fontColor: 'grey6' as TextColor,
    icon: 'settings' as IconType,
    dataCy: 'settings-link'
  });
  menuOptions.push({
    title: 'Log Out',
    onClick: () => signOut(),
    fontColor: 'grey6' as TextColor,
    icon: 'logoutSmall' as IconType,
    dataCy: 'logout-link'
  });

  const isAccountHeaderHidden = ['admin'].some((p) => window.location.pathname.includes(p));

  return (
    <div className="flex w-full items-center">
      {!isAccountHeaderHidden && <AccountScopeHeader user={user} />}
      <div className="flex ml-auto justify-end space-x-3 items-center">
        <PermissibleRender requiredPermissions={[Permission.VIEW_ADMIN_PAGE]}>
          <AdminPageLink />
        </PermissibleRender>
        <PermissibleRender requiredPermissions={[Permission.VIEW_TEAM]}>
          <TeamSettingsPageLink />
        </PermissibleRender>
        <ContactUsLink
          linkText="Contact"
          color="white"
          weight="medium"
        />
        <NotificationCenter />
        <DropdownMenu
          icon="account"
          buttonText="Account"
          buttonTextColor="white"
          options={menuOptions}
          slotProps={{
            paper: {
              style: {
                width: 200
              }
            }
          }}
        />
      </div>
    </div>
  );
};
